import React, { useCallback, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { CreateApplicationDrawer } from "@domains/application/components/CreateApplicationDrawer/CreateApplicationDrawer";
import { ProjectOverviewSidebar } from "@domains/project/components/ProjectOverviewSidebar/ProjectOverviewSidebar";
import { ProjectSecretsTab } from "@domains/project/components/ProjectSecretsTab";
import { ProjectStatsStrip } from "@domains/project/components/ProjectStatsStrip/ProjectStatsStrip";
import { useProjectService } from "@domains/project/services/projectService";
import { CloudConnectionModal } from "@domains/resource/components/CloudConnectionModal/CloudConnectionModal";
import { EmptyProjectHub } from "@domains/resource/components/EmptyProjectHub/EmptyProjectHub";
import { InfraStatusBar } from "@domains/resource/components/InfraStatusBar/InfraStatusBar";
import {
  useInfraConfig,
  useTargetsHealth,
} from "@domains/resource/hooks/useInfraConfig";
import { useResourceRealtime } from "@domains/resource/hooks/useResourceRealtime";
import {
  useProjectResources,
  useResourceActions,
} from "@domains/resource/hooks/useResources";
import type {
  ResourceCreateType,
  ResourceTypeUI,
} from "@domains/resource/types";
import { statusTransitionToast } from "@domains/resource/utils/resourceStatusToasts";
import { useStacksByProject } from "src/domains/application/hooks/useStacksByProject";
import { StackStatus } from "src/domains/application/types/stack";
import { PROJECTS } from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { usePollingApi } from "src/platform/hooks/usePollingApi";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";
import { PaginatedListControls } from "src/platform/ui";
import { useNotification } from "src/shell/contexts/notificationContext";
import {
  getContainerStyles,
  getStatusChipProps,
} from "src/shell/styles/themeUtils";

import { Project } from "../types";
import { activityLabel, activityDotColor } from "../utils/activityLabels";

import { useProjectActivity } from "./ProjectDetails/hooks/useProjectActivity";
import { useProjectStats } from "./ProjectDetails/hooks/useProjectStats";

// ── Helpers ─────────────────────────────────────────────────────

const RESOURCE_TYPE_LABELS: Record<ResourceTypeUI, string> = {
  postgres: "Database",
  redis: "Cache",
  rabbitmq: "Queue",
  nats: "Queue",
  kafka: "Queue",
  pulsar: "Queue",
};

function resourceTypeLabel(type: ResourceTypeUI): string {
  return RESOURCE_TYPE_LABELS[type] ?? type;
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Constants ────────────────────────────────────────────────────

type DrawerState = "app" | "infra" | null;

const TRANSIENT_STACK_STATUSES: StackStatus[] = [
  StackStatus.PROVISIONING,
  StackStatus.BUILDING,
  StackStatus.DEPLOYING,
];

const PROVISIONING_POLL_MS = Number(
  import.meta.env.VITE_PROVISIONING_POLL_MS ?? 5000,
);

// ── Sub-components ───────────────────────────────────────────────

/** Compact app card matching the mockup's `rusha-app-card` pattern. */
function AppCard({
  name,
  meta,
  onClick,
}: {
  name: string;
  meta: string;
  onClick: () => void;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      onClick={onClick}
      sx={{
        p: 1.75,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        cursor: "pointer",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: theme.shadows[2],
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        },
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          color: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          flexShrink: 0,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={600} noWrap>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {meta}
        </Typography>
      </Box>
      <ArrowForwardIcon
        sx={{ color: "text.secondary", flexShrink: 0, fontSize: 18 }}
      />
    </Stack>
  );
}

/** Card wrapper matching the mockup's `rusha-card` pattern. */
function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        p: 2.5,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        mb: 2.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

// ── Main Component ───────────────────────────────────────────────

/**
 * ProjectDetails page — tab-based layout with Overview, Resources,
 * Applications, Activity, and Settings tabs. Matches the project-detail
 * mockup.
 */
export default function ProjectDetails(): React.ReactElement | null {
  const { orgAlias, projectAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
  }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const appNavigation = useAppNavigation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { call: fetchProject } = useRegistryApi<Project>(
    "project",
    "getProject",
  );

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [openDrawer, setOpenDrawer] = useState<DrawerState>(null);
  const [resourceMenuAnchor, setResourceMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [manageMenuAnchor, setManageMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAlias, setEditAlias] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Filter state for Resources, Applications, and Activity tabs
  const [resourceSearch, setResourceSearch] = useState("");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("all");
  const [resourceStatusFilter, setResourceStatusFilter] = useState("all");
  const [appSearch, setAppSearch] = useState("");
  const [appStatusFilter, setAppStatusFilter] = useState("all");
  const [activityTypeFilter, setActivityTypeFilter] = useState("all");
  const [activityTimeFilter, setActivityTimeFilter] = useState("7d");

  // Fetch stacks, resources, and infra config
  const {
    systems,
    total: systemsTotal,
    loading: systemsLoading,
    reload: reloadStacks,
    limit: systemsLimit,
    hasNext: systemsHasNext,
    hasPrevious: systemsHasPrevious,
    setLimit: setSystemsLimit,
    nextPage: nextSystemsPage,
    previousPage: previousSystemsPage,
  } = useStacksByProject(projectAlias ?? "");
  const {
    resources,
    total: resourcesTotal,
    loading: resourcesLoading,
    reload: reloadResources,
    limit: resourcesLimit,
    hasNext: resourcesHasNext,
    hasPrevious: resourcesHasPrevious,
    setLimit: setResourcesLimit,
    nextPage: nextResourcesPage,
    previousPage: previousResourcesPage,
  } = useProjectResources(projectAlias, {
    search: resourceSearch,
    resourceType:
      resourceTypeFilter === "Database"
        ? "postgres"
        : resourceTypeFilter === "Cache"
          ? "redis"
          : resourceTypeFilter === "Queue"
            ? "rabbitmq"
            : undefined,
    status: resourceStatusFilter,
  });
  const {
    infraConfig,
    loading: infraLoading,
    error: infraError,
    reload: reloadInfra,
  } = useInfraConfig(projectAlias);
  const { targets: infraTargets, loading: infraTargetsLoading } =
    useTargetsHealth(projectAlias);
  const { stop, deprovision } = useResourceActions();
  const { showNotification } = useNotification();
  const { deleteProject, updateProject } = useProjectService();

  // WebSocket subscription for real-time resource status changes.
  useResourceRealtime(projectAlias, reloadResources, (transition) => {
    const toast = statusTransitionToast(transition);
    if (toast) {
      showNotification(toast.message, toast.severity);
    }
  });

  // Keep latest reload fn in a ref so the stack poller effect doesn't tear
  // down its interval every time stacks change.
  const reloadStacksRef = useRef(reloadStacks);
  useEffect(() => {
    reloadStacksRef.current = reloadStacks;
  }, [reloadStacks]);

  // Keep latest systems in a ref so the poller interval callback can check
  // in-flight status without re-creating the interval on every data change.
  const systemsRef = useRef(systems);
  useEffect(() => {
    systemsRef.current = systems;
  }, [systems]);

  // Billing polling
  const billingUrl = projectAlias
    ? `/api/v1/projects/${projectAlias}/billing`
    : null;
  const { data: projectBilling, loading: billingLoading } = usePollingApi<{
    totalUsd: number;
    dataQuality: string;
  }>(billingUrl, 60_000);

  const stats = useProjectStats(systems, resources);
  const { activity: projectActivity, loading: activityLoading } =
    useProjectActivity(projectAlias, 5);

  // Load project metadata
  const loadProject = useCallback(() => {
    if (!projectAlias) return;
    setLoading(true);
    setError(null);
    fetchProject({ pathParams: { id: projectAlias } })
      .then((p) => {
        setProject(p);
        if (p) {
          setEditName(p.name);
          setEditAlias(p.alias);
          setEditDescription(p.description ?? "");
        }
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Failed to load project"),
      )
      .finally(() => setLoading(false));
  }, [projectAlias, fetchProject]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // Read tab from URL search params
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (
      tab &&
      [
        "overview",
        "resources",
        "secrets",
        "applications",
        "activity",
        "settings",
      ].includes(tab)
    ) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Auto-open the application drawer when navigated with ?drawer=app
  useEffect(() => {
    if (searchParams.get("drawer") === "app") {
      setOpenDrawer("app");
      const next = new URLSearchParams(searchParams);
      next.delete("drawer");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Provisioning poller
  useEffect(() => {
    const id = window.setInterval(() => {
      const stacksInFlight = systemsRef.current.some((s) =>
        TRANSIENT_STACK_STATUSES.includes(s.status),
      );
      if (stacksInFlight) void reloadStacksRef.current();
    }, PROVISIONING_POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────

  const handleResourceStop = useCallback(
    async (alias: string) => {
      await stop(alias);
      reloadResources();
    },
    [stop, reloadResources],
  );

  const handleResourceDelete = useCallback(
    async (alias: string) => {
      const ok = await deprovision(alias);
      if (ok) reloadResources();
    },
    [deprovision, reloadResources],
  );

  const handleCreateResource = useCallback(
    (type: ResourceCreateType) => {
      if (orgAlias && projectAlias) {
        if (!infraConfig?.cloudProvider) {
          setOpenDrawer("infra");
          return;
        }
        void navigate(
          PROJECTS.RESOURCES_NEW.getPath(orgAlias, projectAlias, type),
        );
      }
    },
    [infraConfig, orgAlias, projectAlias, navigate],
  );

  const handleTabChange = useCallback(
    (_e: React.SyntheticEvent, value: string) => {
      setActiveTab(value);
      const next = new URLSearchParams(searchParams);
      next.set("tab", value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleSaveSettings = useCallback(async () => {
    if (!projectAlias) return;
    setSaving(true);
    const result = await updateProject(projectAlias, {
      name: editName,
      description: editDescription,
    });
    setSaving(false);
    if (result) {
      setProject(result);
    }
  }, [projectAlias, editName, editDescription, updateProject]);

  const handleDelete = useCallback(async () => {
    if (!projectAlias) return;
    setDeleteDialogOpen(false);
    const ok = await deleteProject(projectAlias, true, false);
    if (ok) {
      void navigate(`/organizations/${orgAlias}/projects`);
    }
  }, [projectAlias, deleteProject, navigate, orgAlias]);

  // ── Loading State ─────────────────────────────────────────────

  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        {/* Page header skeleton */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton
            variant="rounded"
            width={56}
            height={24}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rounded"
            width={90}
            height={24}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Skeleton
            variant="rounded"
            width={80}
            height={34}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rounded"
            width={80}
            height={34}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rounded"
            width={100}
            height={34}
            sx={{ borderRadius: 1 }}
          />
        </Stack>

        {/* KPI grid skeleton */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
            mb: 3,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                p: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                borderRadius: 1.5,
              }}
            >
              <Skeleton variant="text" width="60%" height={28} />
              <Skeleton
                variant="text"
                width="40%"
                height={14}
                sx={{ mt: 0.5 }}
              />
              <Skeleton variant="text" width="50%" height={14} />
            </Box>
          ))}
        </Box>

        {/* Tab bar skeleton */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            mb: 3,
            pb: 1,
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="text" width={80 + i * 20} height={32} />
          ))}
        </Stack>

        {/* 2-column grid skeleton */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { md: "2fr 1fr" },
            gap: 3,
          }}
        >
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                borderRadius: 1.5,
              }}
            >
              <Skeleton variant="text" width={80} height={20} sx={{ mb: 2 }} />
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton
                variant="text"
                width="80%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                borderRadius: 1.5,
              }}
            >
              <Skeleton variant="text" width={100} height={20} sx={{ mb: 2 }} />
              <Skeleton
                variant="text"
                width="90%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          </Stack>
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                borderRadius: 1.5,
              }}
            >
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton
                variant="text"
                width="90%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton
                variant="text"
                width="75%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                borderRadius: 1.5,
              }}
            >
              <Skeleton variant="text" width={100} height={20} sx={{ mb: 2 }} />
              <Skeleton
                variant="text"
                width="40%"
                height={20}
                sx={{ mb: 1.5 }}
              />
              <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
          </Stack>
        </Box>
      </Container>
    );
  }

  // ── Error State (inline banner) ───────────────────────────────

  const errorMessage = error ?? (!project ? "Project not found" : null);

  if (errorMessage) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Box
          role="alert"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            p: 1.5,
            mb: 2.5,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.error.main, 0.12),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: "error.main",
            fontSize: "13px",
          }}
        >
          <Typography fontSize="13px" color="error.main" sx={{ flex: 1 }}>
            {errorMessage}. Please try again later.
          </Typography>
          <Button
            size="small"
            onClick={loadProject}
            sx={{
              border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
              borderRadius: 1,
              fontSize: "12px",
              fontWeight: 600,
              color: "error.main",
              textTransform: "none",
            }}
          >
            Try again
          </Button>
        </Box>
      </Container>
    );
  }

  const isEmpty =
    systems.length === 0 &&
    resources.length === 0 &&
    !systemsLoading &&
    !resourcesLoading;

  // After the error check above, project must be non-null.
  // This guard satisfies TypeScript and provides a safe early exit.
  if (!project) return null;

  // ── Main Render ───────────────────────────────────────────────

  return (
    <Container sx={getContainerStyles(theme)}>
      {/* Page header — flat title row + action buttons */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
        >
          <Typography variant="h5" fontWeight={700}>
            {project.name}
          </Typography>
          {project.status &&
            (() => {
              const p = getStatusChipProps(project.status);
              return (
                <Chip
                  label={p.label}
                  color={p.color}
                  variant={p.variant}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              );
            })()}
          <Chip
            label="Rusha Managed"
            variant="outlined"
            size="small"
            sx={{ fontWeight: 500, color: "text.secondary" }}
          />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenDrawer("app")}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Deploy
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              appNavigation.goToAdoptStack(orgAlias!, projectAlias!)
            }
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            + Adopt
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AddIcon sx={{ fontSize: 16 }} />
              </Stack>
            }
            onClick={(e) =>
              setResourceMenuAnchor(e.currentTarget as HTMLButtonElement)
            }
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Resource
            <ArrowDropDownIcon sx={{ fontSize: 16 }} />
          </Button>
          <Menu
            anchorEl={resourceMenuAnchor}
            open={Boolean(resourceMenuAnchor)}
            onClose={() => setResourceMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                setResourceMenuAnchor(null);
                handleCreateResource("db");
              }}
              sx={{ textTransform: "none" }}
            >
              Database
            </MenuItem>
            <MenuItem
              onClick={() => {
                setResourceMenuAnchor(null);
                handleCreateResource("cache");
              }}
              sx={{ textTransform: "none" }}
            >
              Cache
            </MenuItem>
            <MenuItem
              onClick={() => {
                setResourceMenuAnchor(null);
                handleCreateResource("queue");
              }}
              sx={{ textTransform: "none" }}
            >
              Queue
            </MenuItem>
          </Menu>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) =>
              setManageMenuAnchor(e.currentTarget as HTMLButtonElement)
            }
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Manage
            <ArrowDropDownIcon sx={{ fontSize: 16 }} />
          </Button>
          <Menu
            anchorEl={manageMenuAnchor}
            open={Boolean(manageMenuAnchor)}
            onClose={() => setManageMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                setManageMenuAnchor(null);
                handleTabChange({} as React.SyntheticEvent, "settings");
              }}
              sx={{ textTransform: "none" }}
            >
              Rename
            </MenuItem>
            <MenuItem
              onClick={() => {
                setManageMenuAnchor(null);
                handleTabChange({} as React.SyntheticEvent, "settings");
              }}
              sx={{ textTransform: "none" }}
            >
              Edit description
            </MenuItem>
            <MenuItem
              onClick={() => {
                setManageMenuAnchor(null);
                setDeleteDialogOpen(true);
              }}
              sx={{ textTransform: "none", color: "error.main" }}
            >
              Delete project
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 3,
          minHeight: 40,
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            minHeight: 40,
            minWidth: "auto",
            px: 2,
          },
        }}
      >
        <Tab label="Overview" value="overview" />
        <Tab
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>Resources</span>
              {resourcesTotal > 0 && (
                <Typography
                  component="span"
                  sx={{ fontSize: "12px", color: "text.disabled" }}
                >
                  {resourcesTotal}
                </Typography>
              )}
            </Stack>
          }
          value="resources"
        />
        <Tab
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LockIcon sx={{ fontSize: 16 }} />
              <span>Secrets</span>
            </Stack>
          }
          value="secrets"
        />
        <Tab
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>Applications</span>
              {systemsTotal > 0 && (
                <Typography
                  component="span"
                  sx={{ fontSize: "12px", color: "text.disabled" }}
                >
                  {systemsTotal}
                </Typography>
              )}
            </Stack>
          }
          value="applications"
        />
        <Tab label="Activity" value="activity" />
        <Tab label="Settings" value="settings" />
      </Tabs>

      {/* ── Overview Tab ────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <React.Fragment>
          {infraError && (
            <Alert
              severity="error"
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void reloadInfra()}
                    sx={{ textTransform: "none" }}
                  >
                    Retry
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setOpenDrawer("infra")}
                    sx={{ textTransform: "none" }}
                  >
                    Set up
                  </Button>
                </Stack>
              }
              sx={{ mb: 2 }}
            >
              We could not check this project&apos;s infrastructure setup.
              Resource creation is paused until the project target is confirmed.
            </Alert>
          )}
          <InfraStatusBar
            infraConfig={infraConfig}
            targets={infraTargets}
            loading={infraLoading || infraTargetsLoading}
            onManage={() => setOpenDrawer("infra")}
            onSetUp={() => setOpenDrawer("infra")}
          />
          {isEmpty ? (
            <EmptyProjectHub
              infraConfig={infraConfig}
              infraLoading={infraLoading}
              onConnectCloud={() => setOpenDrawer("infra")}
              onSetUp={() => setOpenDrawer("infra")}
              onDeployApp={() => setOpenDrawer("app")}
              onAdoptStack={() =>
                appNavigation.goToAdoptStack(orgAlias!, projectAlias!)
              }
              onCreateResource={handleCreateResource}
              onManage={() => setOpenDrawer("infra")}
            />
          ) : (
            <React.Fragment>
              <ProjectStatsStrip
                totalResources={stats.totalResources}
                runningResources={stats.runningResources}
                healthPct={stats.healthPct}
                healthyCount={stats.healthyCount}
                totalApps={stats.totalSystems}
                runningApps={stats.runningSystems}
                mtdCost={
                  billingLoading
                    ? "…"
                    : !projectBilling
                      ? "—"
                      : `$${projectBilling.totalUsd}`
                }
                mtdCostQuality={projectBilling?.dataQuality ?? "cold"}
                lastActivityLabel={stats.lastDeployedLabel}
                lastActivitySource={stats.lastDeployed ? "deploy" : ""}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { md: "2fr 1fr" },
                  gap: 3,
                }}
              >
                {/* Left column: Resources + Applications */}
                <Box>
                  <SectionCard
                    title="Resources"
                    action={
                      <Button
                        size="small"
                        onClick={() =>
                          handleTabChange(
                            {} as React.SyntheticEvent,
                            "resources",
                          )
                        }
                        sx={{
                          fontSize: "12px",
                          color: "primary.main",
                          fontWeight: 600,
                          textTransform: "none",
                          p: 0,
                        }}
                      >
                        View all
                      </Button>
                    }
                  >
                    {resourcesLoading ? (
                      <Stack spacing={1.5}>
                        {[0, 1, 2].map((i) => (
                          <Skeleton
                            key={i}
                            variant="rounded"
                            height={40}
                            sx={{ borderRadius: 1 }}
                          />
                        ))}
                      </Stack>
                    ) : resources.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No resources yet
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Mode</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {resources.slice(0, 5).map((resource) => {
                              const chipProps = getStatusChipProps(
                                resource.status,
                              );
                              return (
                                <TableRow
                                  key={resource.id}
                                  sx={{
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.03,
                                      ),
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Link
                                      component={RouterLink}
                                      to={PROJECTS.RESOURCES_DETAIL.getPath(
                                        orgAlias ?? "",
                                        projectAlias ?? "",
                                        resource.alias,
                                      )}
                                      sx={{
                                        color: "primary.main",
                                        fontWeight: 600,
                                        textDecoration: "none",
                                      }}
                                    >
                                      {resource.name}
                                    </Link>
                                  </TableCell>
                                  <TableCell>
                                    {resourceTypeLabel(resource.resourceType)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={chipProps.label}
                                      color={chipProps.color}
                                      variant={chipProps.variant}
                                      size="small"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {resource.provisionMode === "shared"
                                      ? "Shared"
                                      : "Dedicated"}
                                  </TableCell>
                                  <TableCell>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      sx={{
                                        opacity: 0,
                                        transition: "opacity 0.15s",
                                        ".MuiTableRow:hover &": { opacity: 1 },
                                      }}
                                    >
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          handleResourceStop(resource.alias)
                                        }
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Stop
                                      </Button>
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          handleResourceDelete(resource.alias)
                                        }
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Delete
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </SectionCard>

                  <SectionCard
                    title="Applications"
                    action={
                      <Button
                        size="small"
                        onClick={() =>
                          appNavigation.goToAdoptStack(orgAlias!, projectAlias!)
                        }
                        sx={{
                          fontSize: "12px",
                          color: "primary.main",
                          fontWeight: 600,
                          textTransform: "none",
                          p: 0,
                        }}
                      >
                        Adopt
                      </Button>
                    }
                  >
                    {systemsLoading ? (
                      <Stack spacing={1.5}>
                        {[0, 1].map((i) => (
                          <Skeleton
                            key={i}
                            variant="rounded"
                            height={56}
                            sx={{ borderRadius: 1 }}
                          />
                        ))}
                      </Stack>
                    ) : systems.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No applications yet
                      </Typography>
                    ) : (
                      <Stack spacing={1.25}>
                        {systems.slice(0, 5).map((system) => (
                          <AppCard
                            key={system.id}
                            name={system.name}
                            meta={`${system.alias} · ${system.status} · ${relativeTime(system.deployedAt)}`}
                            onClick={() => {
                              if (orgAlias && projectAlias) {
                                window.location.href = `/organizations/${orgAlias}/projects/${projectAlias}/stacks/${system.alias}`;
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Box>

                {/* Right column: Project Overview + Recent Activity */}
                <Box>
                  <ProjectOverviewSidebar
                    project={project}
                    activity={projectActivity}
                    activityLoading={activityLoading}
                    onViewAllActivity={() =>
                      handleTabChange({} as React.SyntheticEvent, "activity")
                    }
                  />
                </Box>
              </Box>
            </React.Fragment>
          )}
        </React.Fragment>
      )}

      {/* ── Resources Tab ──────────────────────────────────────── */}
      {activeTab === "resources" && (
        <React.Fragment>
          <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
            <TextField
              size="small"
              placeholder="Search resources..."
              value={resourceSearch}
              onChange={(e) => setResourceSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Select
              size="small"
              value={resourceTypeFilter}
              onChange={(e) => setResourceTypeFilter(e.target.value)}
              sx={{ minWidth: 140 }}
              native
            >
              <option value="all">All types</option>
              <option value="Database">Database</option>
              <option value="Cache">Cache</option>
              <option value="Queue">Queue</option>
            </Select>
            <Select
              size="small"
              value={resourceStatusFilter}
              onChange={(e) => setResourceStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}
              native
            >
              <option value="all">All statuses</option>
              <option value="running">Running</option>
              <option value="stopped">Stopped</option>
              <option value="provisioning">Provisioning</option>
            </Select>
          </Stack>
          <SectionCard title="Resources">
            {resourcesLoading ? (
              <Stack spacing={1.5}>
                {[0, 1, 2].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Stack>
            ) : resources.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No resources yet. Create a database, cache, or queue to get
                started.
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell>Region</TableCell>
                        <TableCell>Last activity</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resources.map((resource) => {
                        const chipProps = getStatusChipProps(resource.status);
                        return (
                          <TableRow
                            key={resource.id}
                            sx={{
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.03,
                                ),
                              },
                            }}
                          >
                            <TableCell>
                              <Link
                                component={RouterLink}
                                to={PROJECTS.RESOURCES_DETAIL.getPath(
                                  orgAlias ?? "",
                                  projectAlias ?? "",
                                  resource.alias,
                                )}
                                sx={{
                                  color: "primary.main",
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                {resource.name}
                              </Link>
                            </TableCell>
                            <TableCell>
                              {resourceTypeLabel(resource.resourceType)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={chipProps.label}
                                color={chipProps.color}
                                variant={chipProps.variant}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell>
                              {resource.provisionMode === "shared"
                                ? "Shared"
                                : "Dedicated"}
                            </TableCell>
                            <TableCell>eu-central-1</TableCell>
                            <TableCell>
                              {relativeTime(resource.updatedAt)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleResourceStop(resource.alias)
                                  }
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "12px",
                                  }}
                                >
                                  Stop
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleResourceDelete(resource.alias)
                                  }
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "12px",
                                  }}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <PaginatedListControls
                  limit={resourcesLimit}
                  total={resourcesTotal}
                  hasNext={resourcesHasNext}
                  hasPrevious={resourcesHasPrevious}
                  onLimitChange={setResourcesLimit}
                  onNext={nextResourcesPage}
                  onPrevious={previousResourcesPage}
                  label="Resources per page"
                />
              </>
            )}
          </SectionCard>
        </React.Fragment>
      )}

      {/* ── Secrets Tab ─────────────────────────────────────────── */}
      {activeTab === "secrets" && orgAlias && projectAlias && (
        <ProjectSecretsTab orgAlias={orgAlias} projectAlias={projectAlias} />
      )}

      {/* ── Applications Tab ────────────────────────────────────── */}
      {activeTab === "applications" && (
        <React.Fragment>
          <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
            <TextField
              size="small"
              placeholder="Search applications..."
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Select
              size="small"
              value={appStatusFilter}
              onChange={(e) => setAppStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}
              native
            >
              <option value="all">All statuses</option>
              <option value="running">Running</option>
              <option value="deploying">Deploying</option>
              <option value="stopped">Stopped</option>
            </Select>
          </Stack>
          <SectionCard title="Applications">
            {systemsLoading ? (
              <Stack spacing={1.5}>
                {[0, 1].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Stack>
            ) : systems.length === 0 ? (
              <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No applications yet. Deploy your first application or adopt an
                  existing stack.
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setOpenDrawer("app")}
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  >
                    New Application
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      appNavigation.goToAdoptStack(orgAlias!, projectAlias!)
                    }
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  >
                    Adopt existing project
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <>
                <Stack spacing={1.25}>
                  {systems
                    .filter((s) => {
                      if (
                        appSearch &&
                        !s.name.toLowerCase().includes(appSearch.toLowerCase())
                      )
                        return false;
                      if (
                        appStatusFilter !== "all" &&
                        s.status.toLowerCase() !== appStatusFilter
                      )
                        return false;
                      return true;
                    })
                    .map((system) => (
                      <AppCard
                        key={system.id}
                        name={system.name}
                        meta={`${system.alias} · ${system.status} · ${relativeTime(system.deployedAt)}`}
                        onClick={() => {
                          if (orgAlias && projectAlias) {
                            window.location.href = `/organizations/${orgAlias}/projects/${projectAlias}/stacks/${system.alias}`;
                          }
                        }}
                      />
                    ))}
                </Stack>
                <PaginatedListControls
                  limit={systemsLimit}
                  total={systemsTotal}
                  hasNext={systemsHasNext}
                  hasPrevious={systemsHasPrevious}
                  onLimitChange={setSystemsLimit}
                  onNext={nextSystemsPage}
                  onPrevious={previousSystemsPage}
                  label="Applications per page"
                />
              </>
            )}
          </SectionCard>
        </React.Fragment>
      )}

      {/* ── Activity Tab ────────────────────────────────────────── */}
      {activeTab === "activity" && (
        <React.Fragment>
          <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
            <Select
              size="small"
              value={activityTypeFilter}
              onChange={(e) => setActivityTypeFilter(e.target.value)}
              sx={{ minWidth: 160, textTransform: "none" }}
              native
            >
              <option value="all">All event types</option>
              <option value="resource">Resource</option>
              <option value="stack">Stack</option>
              <option value="deployment">Deployment</option>
            </Select>
            <Select
              size="small"
              value={activityTimeFilter}
              onChange={(e) => setActivityTimeFilter(e.target.value)}
              sx={{ minWidth: 160 }}
              native
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </Select>
          </Stack>
          <SectionCard title="Recent Activity">
            {activityLoading && projectActivity.length === 0 ? (
              <Stack spacing={2}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="text" height={32} />
                ))}
              </Stack>
            ) : projectActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No activity yet
              </Typography>
            ) : (
              <Stack>
                {projectActivity.map((entry) => (
                  <Stack
                    key={entry.id}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                    sx={{
                      py: 1.25,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: activityDotColor(entry.event),
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography variant="body2">
                        {activityLabel(entry.event)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {relativeTime(entry.createdAt)}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </React.Fragment>
      )}

      {/* ── Settings Tab ────────────────────────────────────────── */}
      {activeTab === "settings" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { md: "2fr 1fr" },
            gap: 3,
          }}
        >
          {/* General settings */}
          <SectionCard title="General">
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Project name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Alias
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={editAlias}
                  disabled
                  helperText="Used in URLs and namespaces."
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Description
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </Box>
            </Stack>
          </SectionCard>

          {/* Danger Zone */}
          <Box
            sx={{
              p: 2.5,
              border: `1px solid ${theme.palette.error.main}`,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.04),
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "error.main",
                mb: 2,
              }}
            >
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Deleting this project removes all resources, applications, and
              data. This action cannot be undone.
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              Delete project
            </Button>
          </Box>
        </Box>
      )}

      {/* Drawers */}
      {openDrawer === "app" && projectAlias && (
        <CreateApplicationDrawer
          isOpen={openDrawer === "app"}
          onClose={() => setOpenDrawer(null)}
          projectAlias={projectAlias}
          infraConfig={infraConfig}
          resources={resources}
          onSuccess={() => {
            // New stack auto-refreshes via WS subscription
          }}
        />
      )}

      <CloudConnectionModal
        open={openDrawer === "infra"}
        onClose={() => setOpenDrawer(null)}
        projectAlias={projectAlias ?? ""}
        onSuccess={() => reloadInfra()}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>
          Permanently Delete Project
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove &quot;{projectAlias}&quot; and all
            associated data, including databases, applications, and K8s
            resources. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
